// @flow
import React, { type Node } from 'react';

import { useUserInputAsSelect } from '@tectonics/prs/user-input-values';
import { Select, SelectOption } from '../../../atoms';
import { RenderTheInstructions } from '../commons/render-the-instructions';

export function RenderTransferNode(): Node {
  const [transferValue = 'bbva_transfer', handleChangeTransfer] = useUserInputAsSelect('transfer-options');
  return (
    <div>
      <Select id="transfer" value={transferValue} onChange={handleChangeTransfer} readOnly={false} name="transfer-options" aria-label="Transfer options">
        {transferOptions.map((element, index) => (
          <SelectOption key={index} value={element[0]}>
            {element[1]}
          </SelectOption>
        ))}
      </Select>
      <RenderTheInstructions eBankingValue={transferValue} eBankingOptions={depositAndTransferOptions} ignoreFirstOption={true} />
    </div>
  );
}

export function RenderDepositNode(): Node {
  const [depositValue = 'bbva_deposit', handleChangeDeposit] = useUserInputAsSelect('deposit-options');
  return (
    <div>
      <Select id="deposit" value={depositValue} onChange={handleChangeDeposit} readOnly={false} name="deposit-options" aria-label="Deposit options">
        {depositOptions.map((element, index) => (
          <SelectOption key={index} value={element[0]}>
            {element[1]}
          </SelectOption>
        ))}
      </Select>
      <RenderTheInstructions eBankingValue={depositValue} eBankingOptions={depositAndTransferOptions} ignoreFirstOption={true} />
    </div>
  );
}

const depositAndTransferOptions = {
  bbva_transfer: [
    'BBVA Continental', 'Selecciona la opción Pagos de Servicios > Instituciones y Emp. > PAGOEFECTIVO', 'Escoge la cuenta según la moneda e ingresa el código CIP.',
  ],
  bcp_transfer: [
    'BCP', 'Selecciona la opción Pagos de Servicios > Instituciones y Emp. > PAGOEFECTIVO', 'Escoge la cuenta según la moneda e ingresa el código CIP.',
  ],
  interbank_transfer: [
    'INTERBANK', 'Selecciona la opción Pagos de Recibos > Diversas Empresas > Empresas > PAGOEFECTIVO.', 'En el menú “Servicio” ingresa el código CIP.',
  ],
  scotiabank_transfer: [
    'SCOTIABANK', 'Selecciona la opción Pagos – Otras instituciones >', 'Escoge la institución según la moneda e ingresa el código CIP.',
  ],
  caja_transfer: [
    'CAJA AREQUIPA', 'Selecciona la opción Pagos de Servicios > Busca la entidad PAGOEFECTIVO.', 'Elige la moneda, ingresa el código CIP y sigue los pasos.',
  ],
  banbif_transfer: [
    'BANBIF', 'Selecciona la opción Pagos de Recibos > Un nuevo Pago > PAGOEFECTIVO.', 'Escoge la cuenta según la moneda e ingresa el código CIP.',
  ],
  bbva_deposit: [
    'BBVA Continental', 'Indica que vas a pagar un servicio de PAGOEFECTIVO, brinda la moneda y el código CIP.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO',
  ],
  tambo_deposit: [
    'TAMBO', 'Realiza tus operaciones en el TAMBO+ a través del agente BBVA',
  ],
  bcp_deposit: [
    'BCP', 'Indica al cajero que vas a pagar un servicio a PAGOEFECTIVO (código de empresa 02186), brinda la moneda y el código CIP.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO', 'IMPORTANTE: Las Agencias BCP (Ventanilla) tienen un cargo adicional de 1 sol.',
  ],
  interbank_deposit: [
    'INTERBANK', 'Indica que vas a pagar un servicio de PAGOEFECTIVO, brinda la moneda y el código CIP.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO.', 'IMPORTANTE: Los Money Market (Vivanda y Plaza Vea) tienen un recargo adicional de 2 soles y sólo aceptan SOLES.',
  ],
  scotiabank_deposit: [
    'SCOTIABANK', 'Indica que vas a pagar un servicio de PAGOEFECTIVO, brinda la moneda y el código CIP.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO.',
  ],
  caja_deposit: [
    'CAJA AREQUIPA', 'Indica que vas a pagar un servicio de PAGOEFECTIVO, brinda la moneda y el código CIP.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO.',
  ],
  banbif_deposit: [
    'BANBIF', 'Indica que vas a pagar un servicio de PAGOEFECTIVO, brinda la moneda y el código CIP.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO.',
  ],
  western_deposit: [
    'WESTERN UNION', 'Indica que vas a pagar un servicio de PAGOEFECTIVO, brinda la moneda y el código CIP.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO.',
  ],
  kasnet_deposit: [
    'AGENTE KASNET', 'Indica que vas a pagar un servicio de PAGOEFECTIVO en soles y brinda el código CIP.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO.',
  ],
  fullcarga_deposit: [
    'FULLCARGA', 'Indique al cajero que va a realizar un pago a la empresa PAGOEFECTIVO y el servicio  PAGOEFECTIVO SOLES.', 'Indique el código CIP, el importe a pagar en soles.', 'Verifica que el comprobante indique el pago a PAGOEFECTIVO.',
  ],
  disashop_deposit: [
    'DISASHOP', 'Indica que vas a pagar un servicio de PAGOEFECTIVO en soles y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  reddigital_deposit: [
    'RED DIGITAL', 'Indica que vas a pagar un servicio a PAGOEFECTIVO y brinda el código CIP y tu nro. De celular.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  moneygram_deposit: [
    'MONEYGRAM - PERU EXPRESS', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  bancoDeLaNacion_deposit: [
    'BANCO DE LA NACIÓN', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP. Solicitalo como pago de factura en línea.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  cajaSullana_deposit: [
    'CAJA SULLANA', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  cajaDelosAndes_deposit: [
    'CAJA DE LOS ANDES', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  cajaPaita_deposit: [
    'CAJA PAITA', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  cajaTrujillo_deposit: [
    'CAJA TRUJILLO', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  bancoAzteca_deposit: [
    'BANCO AZTECA', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  cajaDelSanta_deposit: [
    'CAJA DEL SANTA', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
  cajaRaiz_deposit: [
    'CAJA RAÍZ', 'Indica que vas a pagar un servicio de PAGOEFECTIVO y brinda el código CIP.', 'Verifica que el SMS del comprobante indique el pago a PAGOEFECTIVO.',
  ],
};

// TODO: find a way to use the keys in the above depositAndTransferOptions so we can elimiate these two arrays
const transferOptions = [
  ['bbva_transfer', 'BBVA Continental'],
  ['bcp_transfer', 'BCP'],
  ['interbank_transfer', 'INTERBANK'],
  ['scotiabank_transfer', 'SCOTIABANK'],
  ['caja_transfer', 'CAJA AREQUIPA'],
  ['banbif_transfer', 'BANBIF'],
];

const depositOptions = [
  ['bbva_deposit', 'BBVA Continental'],
  ['tambo_deposit', 'TAMBO'],
  ['bcp_deposit', 'BCP'],
  ['interbank_deposit', 'INTERBANK'],
  ['scotiabank_deposit', 'SCOTIABANK'],
  ['caja_deposit', 'CAJA AREQUIPA'],
  ['banbif_deposit', 'BANBIF'],
  ['western_deposit', 'WESTERN UNION'],
  ['kasnet_deposit', 'AGENTE KASNET'],
  ['fullcarga_deposit', 'FULLCARGA'],
  ['disashop_deposit', 'DISASHOP'],
  ['reddigital_deposit', 'RED DIGITAL'],
  ['moneygram_deposit', 'MONEYGRAM - PERU EXPRESS'],
  ['bancoDeLaNacion_deposit', 'BANCO DE LA NACIÓN'],
  ['cajaSullana_deposit', 'CAJA SULLANA'],
  ['cajaDelosAndes_deposit', 'CAJA DE LOS ANDES'],
  ['cajaPaita_deposit', 'CAJA PAITA'],
  ['cajaTrujillo_deposit', 'CAJA TRUJILLO'],
  ['bancoAzteca_deposit', 'BANCO AZTECA'],
  ['cajaDelSanta_deposit', 'CAJA DEL SANTA'],
  ['cajaRaiz_deposit', 'CAJA RAÍZ'],
];
